import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';

import type { ExtraConfig, IndeedConfig } from './indeed.types';

import { logger } from 'config/logger';
import { queryClient } from 'config/queryClient';
import {
  BrowserExtensionInfoStatus,
  useBrowserExtensionStatusQuery,
} from 'hooks/shared/useBrowserExtensionStatusQuery';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import useFeatureToggles from 'shared/legacy/hooks/useFeatureToggles';
import { useDictionary } from '@/shared/lib/i18n';
import { toast } from '@/shared/lib/toast';
import { SnackbarContext } from 'shared/legacy/contexts/SnackbarContext/SnackbarContext';
import type { SnackbarId } from 'shared/legacy/components/Snackbar';
import { QueryKey } from 'types/query';
import { getConfigFromInterceptedHeaders } from './useIndeedConfig.helper';

const UNKNOWN_STATE: IndeedConfig = {
  isLoggedIn: false,
  status: 'Unknown',
  creditBalance: 0,
  email: null,
};

const LOGGED_OUT_STATE: IndeedConfig = {
  isLoggedIn: false,
  status: 'Logged Out',
  creditBalance: 0,
  email: null,
};

export const useIndeedConfig = (): IndeedConfig & ExtraConfig => {
  const browserExtensionStatusQuery = useBrowserExtensionStatusQuery();
  const { isFeatureToggleEnabled, isLoading: isFeatureTogglesLoading } = useFeatureToggles();
  const { data: currentUser } = useCurrentUserQuery();
  const dictionary = useDictionary();
  const { createSnackbar } = useContext(SnackbarContext);

  /// TODO: --START-- REMOVE AFTER INDEED LICENSE RELEASED
  const filterIndeedCandidatesBasedOnUserLicense = isFeatureToggleEnabled(
    (t) => t.FILTER_INDEED_CANDIDATES_BASED_ON_USER_LICENSE,
  );
  /// TODO: -- END -- REMOVE AFTER INDEED LICENSE RELEASED

  const { data, isInitialLoading, dataUpdatedAt } = useQuery<IndeedConfig>(
    [QueryKey.useIndeedCommonModelQuery, { filterIndeedCandidatesBasedOnUserLicense, userId: currentUser?.id }],
    async () => {
      try {
        if (!currentUser) {
          return LOGGED_OUT_STATE;
        }

        // Primary path: build config from intercepted headers stored by the extension
        const interceptedConfig = await getConfigFromInterceptedHeaders(currentUser, () => {
          const snackbarRef = { id: '' as SnackbarId };
          const onLinkClick = () => toast.dismiss(snackbarRef.id);
          snackbarRef.id = createSnackbar(dictionary.indeedSessionOutdatedDescription(onLinkClick), {
            variant: 'warning',
            persist: true,
            title: dictionary.indeedSessionOutdated,
          });
        });
        if (interceptedConfig) return interceptedConfig;

        // Fallback: original common-model endpoint (currently returning 501)
        // const commonModel = await getCommonModel(currentUser);
        //
        // if (!commonModel) return UNKNOWN_STATE;
        // if (!commonModel.loggedIn)
        //   return {
        //     isLoggedIn: false,
        //     status: 'Logged Out',
        //     creditBalance: 0,
        //     email: null,
        //   } satisfies IndeedConfig;
        //
        // const assignedSubscriptions = commonModel.subscriptionInfo.assignedSubscriptions ?? [];
        // let hasActiveSubscription = assignedSubscriptions.some((s) => s.status === 'ACTIVE');
        // let hasExpiredSubscription = assignedSubscriptions.some((s) => s.status === 'EXPIRED');
        // let creditBalance = commonModel.subscriptionInfo.contactsRemaining;
        // let email = commonModel.emailAddress ?? '';
        //
        // try {
        //   newrelic.addPageAction('IndeedCommonModelV2', {
        //     loggedIn: String(commonModel.loggedIn),
        //     emailAddress: commonModel.emailAddress ?? '',
        //     ctk: String(commonModel.ctk),
        //     tenantId: currentUser?.tenant_id ?? '',
        //     csrfToken: String(commonModel.csrfToken),
        //     csrfParam: String(commonModel.csrfParam),
        //     contactsRemaining: commonModel.subscriptionInfo?.contactsRemaining,
        //     subscriptionKey: commonModel.subscriptionInfo?.subscriptionKey,
        //     subscriptionAssigned: String(commonModel.subscriptionInfo?.subscriptionAssigned),
        //     hasActiveSubscription: String(hasActiveSubscription),
        //     hasExpiredSubscription: String(hasExpiredSubscription),
        //     timestamp: new Date().toISOString(),
        //     agentIdentifier: currentUser?.id ?? '',
        //   });
        // } catch (error) {
        //   logger.error(error, { context: 'IndeedCommonModelLogging' });
        // }
        //
        // if (!hasActiveSubscription && !filterIndeedCandidatesBasedOnUserLicense) {
        //   const subscriptionAdmin = await getSubscriptionAdmin(currentUser);
        //   const adminSubscriptionState = getAdminFallbackSubscriptionState(commonModel, subscriptionAdmin);
        //
        //   if (!adminSubscriptionState) {
        //     return LOGGED_OUT_STATE;
        //   }
        //
        //   hasActiveSubscription = adminSubscriptionState.hasActiveSubscription;
        //   hasExpiredSubscription = hasExpiredSubscription || adminSubscriptionState.hasExpiredSubscription;
        //   creditBalance = adminSubscriptionState.creditBalance;
        //   email = adminSubscriptionState.email;
        // }
        //
        // if (!email) {
        //   logger.error('Indeed user is logged in but doesn’t have an email.');
        // }
        //
        // const indeedApiKeyHeaderName = await decryptString('iMMoF9BO61y8w2UtGQ7tx6pW/tSdzMXB/utwuh7y.apDozr3Z4I8HpX7m');
        // const indeedApiKey = await decryptString(
        //   'kOvCIj3Z8ogZMJXGSVt4WqKRCCtcgP6om9E40fi3F2Qbbokh7RcgNn2oAgjOacISrB9kkzxC6sbYg5gGl0NGGE/l2yF6QZOLySBE53F0ySE=.tr5BanGWF4hZ4lBl',
        // );
        //
        // const headers: Record<string, string> = {
        //   'content-type': 'application/json',
        //   'indeed-ctk': commonModel.ctk,
        //   'indeed-client-sub-app': 'rezemp-discovery',
        //   'indeed-client-sub-app-component': './Root',
        //   [indeedApiKeyHeaderName]: indeedApiKey,
        //   referer: 'https://resumes.indeed.com/',
        // };
        //
        // return {
        //   isLoggedIn: true,
        //   headers,
        //   status: 'Logged In',
        //   email,
        //   csrfParam: commonModel.csrfParam,
        //   csrfToken: commonModel.csrfToken,
        //   creditBalance,
        //   hasExpiredSubscription,
        //   hasActiveSubscription,
        // } satisfies IndeedConfig;
        return UNKNOWN_STATE;
      } catch (e) {
        logger.error(e);

        return UNKNOWN_STATE;
      }
    },
    {
      enabled:
        browserExtensionStatusQuery.data.status === BrowserExtensionInfoStatus.Active && !isFeatureTogglesLoading,
      initialData: UNKNOWN_STATE,
      initialDataUpdatedAt: 0,
      retry: 0,
    },
  );

  const _isInitialLoading =
    browserExtensionStatusQuery.isInitialLoading ||
    (browserExtensionStatusQuery.data.status === BrowserExtensionInfoStatus.Active &&
      (isInitialLoading || dataUpdatedAt === 0));

  return useMemo(() => {
    return {
      ...data,
      isInitialLoading: _isInitialLoading,
      isIndeedCreditsOutOfSync: data.isLoggedIn && data.creditBalance <= 0 && !_isInitialLoading,
      getCurrentValue: () =>
        queryClient.getQueryData<IndeedConfig>([
          QueryKey.useIndeedCommonModelQuery,
          { filterIndeedCandidatesBasedOnUserLicense, userId: currentUser?.id },
        ]) as IndeedConfig,
    };
  }, [data, _isInitialLoading, filterIndeedCandidatesBasedOnUserLicense, currentUser?.id]);
};

// const getCommonModel = async (currentUser?: CurrentUser): Promise<CommonModel | null> => {
//   try {
//     const response = await Promise.race([
//       browserExtensionClient.sendMessage('proxyHttpRequest', {
//         url: 'https://resumes.indeed.com/api/common-model',
//         method: 'GET',
//       }),
//       new Promise((_, reject) =>
//         setTimeout(() => {
//           reject(
//             new BrowserExtensionTimeoutException(
//               "proxyHttpRequest, { url: 'https://resumes.indeed.com/api/common-model', method: 'GET', }",
//             ),
//           );
//         }, 10_000),
//       ),
//     ]);
//
//     const result = JSON.parse(response.body);
//
//     try {
//       newrelic.addPageAction('IndeedCommonModelRequest', {
//         emailAddress: currentUser?.email ?? '',
//         tenantId: currentUser?.tenant_id ?? '',
//         response: result,
//         timestamp: new Date().toISOString(),
//         status: 'success',
//       });
//     } catch (error) {
//       logger.error(error, { context: 'IndeedCommonModelRequestLogging' });
//     }
//
//     return result;
//   } catch (err) {
//     try {
//       newrelic.addPageAction('IndeedCommonModelRequest', {
//         emailAddress: currentUser?.email ?? '',
//         tenantId: currentUser?.tenant_id ?? '',
//         response: JSON.stringify(err),
//         timestamp: new Date().toISOString(),
//         status: 'error',
//       });
//     } catch (error) {
//       logger.error(error, { context: 'IndeedCommonModelRequestLogging' });
//     }
//
//     if (err instanceof BrowserExtensionTimeoutException) {
//       logger.error(err);
//
//       return null;
//     }
//
//     throw err;
//   }
// };
